// dateTz.js
// Utilities to format and compare dates in a specific IANA timezone without drifting days.
// Avoids external deps by using Intl.DateTimeFormat and formatToParts.

/**
 * Normalize input to a Date object, if possible.
 * - If input is a date-only string (YYYY-MM-DD), construct a Date in local time at 00:00.
 * - Otherwise, use the built-in Date parser.
 * @param {Date|string|number|null|undefined} d
 * @returns {Date|null}
 */
export function toDate(d) {
  if (!d) return null;
  if (d instanceof Date) return isNaN(d.getTime()) ? null : d;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const parts = d.split('-');
    const yRaw = parts[0];
    const mRaw = parts[1];
    const dRaw = parts[2];
    const y = Number.isFinite(Number(yRaw)) ? Number(yRaw) : new Date().getFullYear();
    const m = Number.isFinite(Number(mRaw)) ? Number(mRaw) : 1;
    const day = Number.isFinite(Number(dRaw)) ? Number(dRaw) : 1;
    return new Date(y, m - 1, day);
  }
  // Normalize common non-ISO forms like 'YYYY-MM-DD HH:mm:ss+00' or with fractional seconds to ISO
  if (typeof d === 'string') {
    let s = d.trim();

    // 1) Trim excessive fractional seconds to milliseconds to satisfy native Date parse
    //    Examples from Supabase: '2025-08-29T15:48:25.217361+00:00'
    //    We convert to:          '2025-08-29T15:48:25.217+00:00'
    //    Safe no-op for strings without fractional seconds
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+(Z|[+-]\d{2}:?\d{2})?$/.test(s)) {
      s = s.replace(/\.(\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2})?$)/, '.$1');
    }
    // Replace the first space between date and time (if present) with 'T'
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?([+-]\d{2}(?::?\d{2})?|Z)?$/.test(s)) {
      s = s.replace(' ', 'T');
    }
    // Normalize timezone offsets:
    // - '+0000'  -> '+00:00'
    // Only add colon if there isn't one already
    s = s.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    let dtNorm = new Date(s);
    if (!isNaN(dtNorm.getTime())) return dtNorm;
    // Fallback: manual parse for ISO-like strings with long fractional seconds or odd offsets
    const m = s.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|([+-])(\d{2}):?(\d{2})?)?$/,
    );
    // removed debug logging
    if (m) {
      const [, ys, mos, ds, hs, mis, ss, frac, tzAll, sign, ohs, oms] = m;
      const y = Number(ys);
      const mo = Number(mos) - 1;
      const day = Number(ds);
      const h = Number(hs);
      const mi = Number(mis);
      const sec = Number(ss);
      const ms = frac ? Number((frac + '000').slice(0, 3)) : 0; // trim/pad to milliseconds
      let ts = Date.UTC(y, mo, day, h, mi, sec, ms);
      if (tzAll && tzAll !== 'Z') {
        const offH = Number(ohs || '0');
        const offM = Number(oms || '0');
        const offMin = offH * 60 + offM;
        // ISO offset meaning: local = UTC + offset. To get UTC from local, subtract (+) and add (-).
        const dir = sign === '+' ? 1 : -1;
        ts -= dir * offMin * 60 * 1000;
      }
      dtNorm = new Date(ts);
      return isNaN(dtNorm.getTime()) ? null : dtNorm;
    }

    // Last-chance fallback: if it still didn't match, attempt to coerce by trimming
    // any fractional seconds beyond milliseconds and retry native Date.
    // Keep timezone portion intact.
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+/.test(s)) {
      const coerced = s.replace(/(\.\d{3})\d+/, '$1');
      const dt = new Date(coerced);
      if (!isNaN(dt.getTime())) return dt;
    }
    return null;
  }
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
}

/**
 * Return YYYY-MM-DD for the given date in the specified timezone.
 * If the input is a date-only string, returns it as-is to avoid shifting.
 * @param {Date|string|number|null|undefined} d
 * @param {string} timeZone
 * @returns {string}
 */
export function toYMDInZone(d, timeZone) {
  if (!d) return '';
  // If an exact date-only string (no time), return as-is to avoid shifting
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    return d;
  }
  const dt = toDate(d);
  if (!dt) {
    // Manual parse of ISO with timezone to compute correct epoch → then format in target TZ
    if (typeof d === 'string') {
      const s = d.trim();
      const m = s.match(
        /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?(Z|([+-])(\d{2}):?(\d{2})?)?$/,
      );
      if (m) {
        const [, ys, mos, ds, hs, mis, ssOpt, frac, tzAll, sign, ohs, oms] = m;
        const y = Number(ys);
        const mo = Number(mos) - 1;
        const day = Number(ds);
        const h = Number(hs);
        const mi = Number(mis);
        const sec = ssOpt ? Number(ssOpt) : 0;
        const ms = frac ? Number((frac + '000').slice(0, 3)) : 0;
        let ts = Date.UTC(y, mo, day, h, mi, sec, ms);
        if (tzAll && tzAll !== 'Z') {
          const offH = Number(ohs || '0');
          const offM = Number(oms || '0');
          const offMin = offH * 60 + offM;
          const dir = sign === '+' ? 1 : -1;
          ts -= dir * offMin * 60 * 1000;
        }
        const dt2 = new Date(ts);
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).formatToParts(dt2);
        const obj = Object.fromEntries(parts.map((p) => [p.type, p.value]));
        return `${obj.year}-${obj.month}-${obj.day}`;
      }
      // Fallback: if it looks like an ISO-like string, return its date part
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    }
    return '';
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(dt);
  const obj = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  // en-US gives month/day/year; ensure YYYY-MM-DD order
  return `${obj.year}-${obj.month}-${obj.day}`;
}

/**
 * Human-friendly local date string in the specified timezone.
 * Falls back to YYYY-MM-DD if Intl fails.
 * @param {Date|string|number|null|undefined} d
 * @param {string} timeZone
 * @param {Intl.LocalesArgument} [locale]
 * @returns {string}
 */
export function formatDateInZone(d, timeZone, locale = undefined) {
  if (!d) return '';
  const dt = toDate(d);
  if (!dt) {
    // Try manual parse so we can still format correctly in the provided timeZone
    if (typeof d === 'string') {
      const s = d.trim();
      const m = s.match(
        /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|([+-])(\d{2}):?(\d{2})?)?$/,
      );
      if (m) {
        const [, ys, mos, ds, hs, mis, ss, frac, tzAll, sign, ohs, oms] = m;
        const y = Number(ys);
        const mo = Number(mos) - 1;
        const day = Number(ds);
        const h = Number(hs);
        const mi = Number(mis);
        const sec = Number(ss);
        const ms = frac ? Number((frac + '000').slice(0, 3)) : 0;
        let ts = Date.UTC(y, mo, day, h, mi, sec, ms);
        if (tzAll && tzAll !== 'Z') {
          const offH = Number(ohs || '0');
          const offM = Number(oms || '0');
          const offMin = offH * 60 + offM;
          const dir = sign === '+' ? 1 : -1;
          ts -= dir * offMin * 60 * 1000;
        }
        const dt2 = new Date(ts);
        try {
          return new Intl.DateTimeFormat(locale || undefined, {
            timeZone,
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }).format(dt2);
        } catch (_) {
          const ymd = toYMDInZone(new Date(ts), timeZone);
          return ymd;
        }
      }
      // Fallback to date slice if an ISO-like string was provided
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    }
    return '';
  }
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      timeZone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(dt);
  } catch (_) {
    return toYMDInZone(d, timeZone);
  }
}

/**
 * Compare two dates for same calendar day in the specified timezone.
 * @param {Date|string|number} a
 * @param {Date|string|number} b
 * @param {string} timeZone
 */
export function isSameDayInZone(a, b, timeZone) {
  return toYMDInZone(a, timeZone) === toYMDInZone(b, timeZone);
}
